// Home-page Sockudo connection probe. See doc/adr/0022.
//
// Purpose: generate *real* concurrent WebSocket connections against a Sockudo
// instance (from organic home-page traffic) so its connection capacity can be
// measured under production-like conditions — the #1 success criterion in
// ADR-0022. Connection count is exactly the axis the cheap hosted-Pusher tier
// caps us on, so this measures the thing that actually blocks the migration.
//
// Safety contract (this runs on the highest-traffic page in the app):
//   * It uses a DEDICATED config (gon.sockudo_probe), never the PUSHER_* /
//     gon.pusher_* vars that drive the real seating & attendees consumers, so
//     it can neither repoint production real-time traffic nor accidentally
//     open connections to the hosted Pusher account (@sockudo/client would
//     otherwise default wsHost to ws.pusherapp.com — see sockudo_client_global).
//   * It only ever acts when an explicit Sockudo host AND key are present.
//   * It subscribes to a single PUBLIC channel — no authEndpoint / pubsub_auth
//     load, and no exposure to the private-channel authorization gap.
//   * Every failure is swallowed. A dead or overloaded Sockudo must be
//     completely invisible to the visitor.
import Sockudo from "@sockudo/client"

export interface SockudoProbeConfig {
  key: string
  wsHost: string
  wsPort?: number
  forceTLS?: boolean
  channel: string
}

// Kept only to prevent the client (and its open socket) from being
// garbage-collected while the page is alive; the browser tears the connection
// down on navigation/unload, which is exactly the churn we want to measure.
declare global {
  interface Window {
    __sockudoProbe?: Sockudo
  }
}

export function startSockudoProbe(config?: SockudoProbeConfig | null): Sockudo | undefined {
  // Safety interlock: never construct a client without an explicit Sockudo
  // host + key. No config → no connection, no chance of hitting hosted Pusher.
  if (!config || !config.wsHost || !config.key) return undefined

  try {
    const client = new Sockudo(config.key, {
      wsHost: config.wsHost,
      wsPort: config.wsPort,
      wssPort: config.wsPort,
      forceTLS: Boolean(config.forceTLS),
      enabledTransports: ["ws", "wss"],
      disableStats: true,
      // protocolVersion intentionally unset → Protocol V1 (protocol=7), the
      // same wire protocol production already speaks (see ADR-0022).
    })

    // Swallow connection errors: the probe must be invisible to the visitor.
    client.connection.bind("error", (err: unknown) => {
      console.debug("[sockudo-probe] connection error", err)
    })

    // Hold one public-channel subscription open. We bind nothing and never
    // publish — this is a connection-capacity probe, not a messaging test.
    client.subscribe(config.channel)

    return client
  } catch (err) {
    console.debug("[sockudo-probe] init failed", err)
    return undefined
  }
}

// Auto-start when loaded as a page entrypoint. Guarded on `window` so importing
// this module in a non-browser (unit test) context is a no-op.
if (typeof window !== "undefined") {
  const config = (window as Window & { gon?: { sockudo_probe?: SockudoProbeConfig } }).gon
    ?.sockudo_probe
  const client = startSockudoProbe(config)
  if (client) window.__sockudoProbe = client
}
